import { createFeature, createReducer, on } from '@ngrx/store';

import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { Activity } from 'app/shared/features/activity-selector/models';
import { Container } from 'app/shared/features/container-selector/models';
import { Locations } from 'app/shared/features/location-selector/models';
import { done, getInitialProgressState, ProgressState, running } from 'app/shared/models';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';
import { ComponentState } from 'app/state/manual-flow/select-state';

import {
    applyResultRegistrationFlowRegistrationDataFromStorage,
    createContainerMeasurementInResultRegistrationFlow,
    createRegistrationInResultRegistrationFlow,
    initResultRegistrationFlow,
    initResultRegistrationFlowSuccess,
    resetResultRegistrationFlowRegistrationData,
    selectActivityInResultRegistrationFlow,
    setActivityInResultRegistrationFlow,
    setAmountAndProductBoxInResultRegistrationFlow,
    setAmountResultInResultRegistrationFlow,
    setBatchInResultRegistrationFlow,
    setContainerInResultRegistrationFlow,
    setEmployeeInResultRegistrationFlow,
    setEmployeeInResultRegistrationFlowWithoutMeasurementType,
    setLocationInResultRegistrationFlow,
    setLocationInResultRegistrationFlowWithoutMeasurementType,
    setResultRegistrationFlowComponentState,
} from './result-registration-flow.actions';

export const resultRegistrationFlowFeatureKey = 'resultRegistrationFlow';

export interface ResultRegistrationFlowRegistrationData {
    employeeNumber: number | undefined;
    batchNumber: number | undefined;
    containers: Container[];
    amountResult: number | undefined;
    productBoxRegistrations: ProductBoxRegistration[];
    location: Locations | undefined;
}

export interface ResultRegistrationFlowState {
    data: ResultRegistrationFlowRegistrationData;
    initializationProgress: ProgressState;
    resultRegistrationComponentState: ComponentState;
    activity: Activity | undefined;
}

export const emptyResultRegistrationFlowRegistrationData: ResultRegistrationFlowRegistrationData = {
    amountResult: undefined,
    batchNumber: undefined,
    employeeNumber: undefined,
    productBoxRegistrations: [],
    containers: [],
    location: undefined,
};

export const initialState: ResultRegistrationFlowState = {
    data: emptyResultRegistrationFlowRegistrationData,
    initializationProgress: getInitialProgressState(),
    resultRegistrationComponentState: ComponentState.SelectActivity,
    activity: undefined,
};

export const reducer = createReducer(
    initialState,
    on(initResultRegistrationFlow, (state) => {
        if (state.initializationProgress.done) return state;
        return { ...state, initializationProgress: running() };
    }),
    on(initResultRegistrationFlowSuccess, (state) => ({ ...state, initializationProgress: done() })),
    on(applyResultRegistrationFlowRegistrationDataFromStorage, (state, { registrationDataFromStorage }) => ({
        ...state,
        initializationProgress: done(),
        data: registrationDataFromStorage ?? emptyResultRegistrationFlowRegistrationData,
    })),
    on(resetResultRegistrationFlowRegistrationData, resetApiConfig, (state) => ({
        ...state,
        data: emptyResultRegistrationFlowRegistrationData,
        activity: state.activity,
    })),
    on(selectActivityInResultRegistrationFlow, (state) => ({
        ...state,
        resultRegistrationComponentState: ComponentState.SelectActivity,
    })),
    on(setActivityInResultRegistrationFlow, (state, { activity }) => ({
        ...state,
        activity: activity,
    })),
    on(setResultRegistrationFlowComponentState, (state, { componentState }) => ({
        ...state,
        resultRegistrationComponentState: componentState,
    })),
    on(setBatchInResultRegistrationFlow, (state, { batchNumber }) =>
        state.data.batchNumber === batchNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, batchNumber },
              },
    ),
    on(setLocationInResultRegistrationFlow, (state, location) =>
        state.data.location === location
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      location: location,
                  },
              },
    ),
    on(setLocationInResultRegistrationFlowWithoutMeasurementType, (state, location) =>
        state.data.location === location
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      location: location,
                  },
              },
    ),
    on(setContainerInResultRegistrationFlow, (state, { containerList }) =>
        state.data.containers === containerList
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      containers: containerList,
                  },
              },
    ),
    on(setAmountResultInResultRegistrationFlow, (state, { amountResult }) =>
        state.data.amountResult === amountResult
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      amountResult: amountResult,
                  },
              },
    ),
    on(setAmountAndProductBoxInResultRegistrationFlow, (state, { updatedProductBoxRegistrations }) =>
        state.data.productBoxRegistrations === updatedProductBoxRegistrations
            ? state
            : {
                  ...state,
                  data: {
                      ...state.data,
                      productBoxRegistrations: updatedProductBoxRegistrations,
                      amountResult: undefined,
                  },
              },
    ),
    on(setEmployeeInResultRegistrationFlow, (state, { employeeNumber }) =>
        state.data.employeeNumber === employeeNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, employeeNumber },
              },
    ),
    on(setEmployeeInResultRegistrationFlowWithoutMeasurementType, (state, { employeeNumber }) =>
        state.data.employeeNumber === employeeNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, employeeNumber },
              },
    ),
    on(createContainerMeasurementInResultRegistrationFlow, (state) => ({
        ...state,
        state: {
            state: ComponentState.CreateContainerMeasurement,
        },
    })),
    on(createRegistrationInResultRegistrationFlow, (state) => ({
        ...state,
        state: {
            state: ComponentState.CreateRegistration,
        },
    })),
);

export const resultRegistrationFlowFeature = createFeature({
    name: resultRegistrationFlowFeatureKey,
    reducer,
});
